import * as React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {MAX_TILES_PER_ROW} from 'src/components/analytics-genesis/byod/constants';
import {ROW_TYPE} from 'src/types/byod';
import type {Row} from 'src/types/byod';

import type {useDragIndicator} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/hooks/use-drag-indicator';
import {TileRow, type InvalidHoverReason} from './tile-row';
import type {DragType} from './dnd-config';


export type DroppableTileRowProps = {
  rowId: string;
  row: Row;
  dropType: DragType;
  activeDragType: DragType | null;
  dragSourceRowId: string | null;
  isRowAtCapacity: boolean;
  indicatorTarget: ReturnType<typeof useDragIndicator>['indicatorTarget'];
  /**
   * True when a cross-row tile placeholder is rendered inside `tiles` —
   * the placeholder IS the visual "drop here" affordance, so the inline
   * vertical indicator line in TileRow becomes redundant. Hide it to
   * avoid double-feedback at the same insertion point.
   */
  suppressInlineIndicator: boolean;
  hoveredInvalid: {rowId: string; reason: InvalidHoverReason} | null;
  onInvalidHover: (
    rowId: string | null,
    reason: InvalidHoverReason | null,
  ) => void;
  rowClassName: string;
  tiles: React.ReactNode;
  trailingContent: React.ReactNode;
};

/**
 * Wraps a tile row with the drop-target derivation, invalid-hover tracking,
 * and pill-label translation. The row itself is NOT a `useDroppable` —
 * `isDropTarget` is derived from the indicator hook's state instead. A
 * row-level droppable would compete with the child sortable items for
 * collision detection and could swallow drops.
 */
export function DroppableTileRow({
  rowId,
  row,
  dropType,
  activeDragType,
  dragSourceRowId,
  isRowAtCapacity,
  indicatorTarget,
  suppressInlineIndicator,
  hoveredInvalid,
  onInvalidHover,
  rowClassName,
  tiles,
  trailingContent,
}: DroppableTileRowProps) {
  const {t} = useLabelTranslation();
  const maxTiles = MAX_TILES_PER_ROW[row.type] ?? Infinity;
  const isIncompatibleDropTarget =
    (activeDragType !== null && activeDragType !== dropType) || isRowAtCapacity;
  let invalidHoverReason: InvalidHoverReason | null = null;
  if (isRowAtCapacity) {
    invalidHoverReason = {kind: 'row-full', rowType: row.type, max: maxTiles};
  } else if (activeDragType !== null && activeDragType !== dropType) {
    invalidHoverReason = {kind: 'type-mismatch', rowType: row.type};
  }
  const showInvalidPill = hoveredInvalid?.rowId === rowId;

  const isDropTarget =
    indicatorTarget !== null &&
    indicatorTarget.droppableId === rowId &&
    indicatorTarget.orientation === 'vertical';

  let pillLabel = '';
  if (invalidHoverReason) {
    if (invalidHoverReason.kind === 'type-mismatch') {
      pillLabel =
        invalidHoverReason.rowType === ROW_TYPE.KPI
          ? t('KPI_tiles_only', 'KPI tiles only')
          : t('Charts_tables_only', 'Charts & tables only');
    } else {
      pillLabel = t('Row_full_countcount', {
        count: invalidHoverReason.max,
        defaultValue: `Row full · ${invalidHoverReason.max}/${invalidHoverReason.max}`,
      });
    }
  }

  return (
    <TileRow
      rowId={rowId}
      rowType={row.type}
      indicatorTarget={indicatorTarget}
      isDropTarget={isDropTarget}
      suppressInlineIndicator={suppressInlineIndicator}
      isIncompatibleDropTarget={isIncompatibleDropTarget}
      invalidHoverReason={invalidHoverReason}
      showInvalidPill={showInvalidPill}
      pillLabel={pillLabel}
      onInvalidHover={onInvalidHover}
      rowClassName={rowClassName}
      tiles={tiles}
      trailingContent={trailingContent}
    />
  );
}
